import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const LABEL_SOURCE_IDS = new Set([
  "composite",
  "mapbox",
  "mapbox-streets"
]);

const TARGET_LAYER_IDS = new Set([
  "country-label",
  "state-label",
  "settlement-subdivision-label",
  "settlement-minor-label",
  "settlement-major-label",
  "poi-label",
  "airport-label",
  "road-label",
  "waterway-label",
  "natural-line-label",
  "natural-point-label",
  "water-line-label",
  "water-point-label"
]);

const normalizeLang = (lang) => {
  if (!lang) return 'de';
  const baseLang = lang.split('-')[0].split('_')[0].toLowerCase();
  return baseLang === 'de' || baseLang === 'en' ? baseLang : 'de';
};

const exprFor = (lang) => {
  const normalizedLang = normalizeLang(lang);
  return [
    "coalesce",
    ["get", `name_${normalizedLang}`],
    ["get", `name:${normalizedLang}`],
    ["get", "name_en"],
    ["get", "name"]
  ];
};

export const useMapLanguage = (mapRef) => {
  const { i18n } = useTranslation();
  const [mapReady, setMapReady] = useState(false);
  const lastApplyMsRef = useRef(0);
  const styleDataBurstRef = useRef({ lastTs: 0, count: 0 });

  const getNormalizedLang = () => {
    const lang = i18n.language || i18n.resolvedLanguage || 'de';
    return normalizeLang(lang);
  };

  const currentLang = useRef(getNormalizedLang());

  useEffect(() => {
    if (mapRef.current) {
      setMapReady(true);
      return;
    }

    const interval = setInterval(() => {
      if (mapRef.current) {
        setMapReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [mapRef]);

  const applyLanguage = (map, lang) => {
    if (!map || !map.isStyleLoaded()) return;

    const style = map.getStyle();
    if (!style?.layers) return;

    const normalizedLang = normalizeLang(lang);
    const expr = exprFor(normalizedLang);

    const debug = import.meta.env.MODE === 'development' && localStorage.getItem('debugMapLang') === '1';
    const started = debug ? performance.now() : 0;

    const updatedLayerIds = [];
    style.layers
      .filter(l =>
        l.type === "symbol" &&
        map.getLayoutProperty(l.id, "text-field") &&
        TARGET_LAYER_IDS.has(l.id)
      )
      .forEach(l => {
        try {
          map.setLayoutProperty(l.id, "text-field", expr);
          if (debug) updatedLayerIds.push(l.id);
        } catch (error) {
          console.warn(`Failed to set language for layer ${l.id}:`, error);
        }
      });

    if (debug) {
      const took = performance.now() - started;
      lastApplyMsRef.current = took;
      const layers = updatedLayerIds.length;
      console.info('[useMapLanguage] applyLanguage', { lang: normalizedLang, layers, tookMs: Math.round(took) });
    }
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const lang = getNormalizedLang();
    currentLang.current = lang;

    const onStyleData = () => {
      const debug = import.meta.env.MODE === 'development' && localStorage.getItem('debugMapLang') === '1';
      if (debug) {
        const now = performance.now();
        const burst = styleDataBurstRef.current;
        if (now - burst.lastTs < 500) {
          burst.count += 1;
        } else {
          burst.count = 1;
        }
        burst.lastTs = now;
        if (burst.count === 10) {
          console.info('[useMapLanguage] styledata burst (10 in <500ms)');
        }
      }
      applyLanguage(map, currentLang.current);
    };
    const onLoad = () => {
      if (map.isStyleLoaded()) {
        applyLanguage(map, currentLang.current);
      }
    };

    if (map.isStyleLoaded()) {
      applyLanguage(map, currentLang.current);
    } else {
      map.once("styledata", () => {
        applyLanguage(map, currentLang.current);
      });
    }

    map.on("load", onLoad);
    map.on("styledata", onStyleData);

    return () => {
      map.off("load", onLoad);
      map.off("styledata", onStyleData);
    };
  }, [mapRef, i18n.language, i18n.resolvedLanguage, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const newLang = getNormalizedLang();
    if (newLang !== currentLang.current) {
      currentLang.current = newLang;

      if (map.isStyleLoaded()) {
        applyLanguage(map, newLang);
      } else {
        map.once("styledata", () => {
          applyLanguage(map, newLang);
        });
      }
    }
  }, [i18n.language, i18n.resolvedLanguage, mapRef]);
};
